import React, { useState, useEffect, useRef } from 'react'
import { useField } from 'formik'
import { CircularProgress } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import Assets from 'app/configs/assetConfigs'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { makeStyles } from '@material-ui/core'
import CheckBox from 'app/componentV4/CheckBox'
import { isNil } from 'ramda'
const useStyles = makeStyles({
  dropdown: {
    maxHeight: '300px',
  },
})

const MAX_WIDTH_BEACON_IMAGE = 15

const BeaconSelector = ({
  options,
  name,
  fetchData,
  isLoading,
  disabled = false,
}) => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)
  const [field, meta, helpers] = useField(name)
  const isError = !isNil(meta.error) && meta.touched && !disabled
  const dropdownRef = useRef(null)
  const isAllSelected = field.value.length === options.length
  const hasValue = field.value.length > 0

  const handleSelect = (option) => {
    const newSelectedOptions = field.value.includes(option)
      ? field.value.filter((o) => o.id !== option.id)
      : [...field.value, option]
    helpers.setValue(newSelectedOptions)
  }

  const handleSelectAll = () => {
    if (field.value.length === options.length) {
      helpers.setValue([]) // Deselect all
    } else {
      helpers.setValue(options) // Select all
    }
  }

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(fetchData, [])

  // Display only 2 selected items and show "more X items" if more than 2 are selected
  const renderSelectedItems = () => {
    if (field.value.length > 2) {
      const [first, second] = field.value.slice(0, 2)
      const remainingCount = field.value.length - 2
      return (
        <div className="flex flex-wrap gap-2">
          <div className="flex space-x-2" key={first.id}>
            <img
              src={
                Assets.manageBeacon.colors[first.color] ||
                Assets.manageBeacon.colors.default
              }
              width={MAX_WIDTH_BEACON_IMAGE}
              alt="Beacon image"
            />
            <label className="m-auto">{first.name}</label>
          </div>
          <div className="flex space-x-2" key={second.id}>
            <img
              src={
                Assets.manageBeacon.colors[second.color] ||
                Assets.manageBeacon.colors.default
              }
              width={MAX_WIDTH_BEACON_IMAGE}
              alt="Beacon image"
            />
            <label className="m-auto">{second.name}</label>
          </div>
          <div className="px-4 text-blue-950 flex items-center">
            <FormattedMessage
              id="beaconSelector.andMore"
              values={{
                items: remainingCount,
              }}
            />
          </div>
        </div>
      )
    } else {
      return field.value.map((item) => (
        <div className="flex space-x-2" key={item.id}>
          <img
            src={
              Assets.manageBeacon.colors[item.color] ||
              Assets.manageBeacon.colors.default
            }
            width={MAX_WIDTH_BEACON_IMAGE}
            alt="Beacon image"
          />
          <label className="m-auto">{item.name}</label>
        </div>
      ))
    }
  }

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'p-14 h-48 rounded-8 border border-grey-input w-full bg-white  h-auto sm:flex sm:space-x-2 sm:space-y-0 space-y-2',
          hasValue ? 'text-blue-950' : 'text-grey-300',
          isLoading ? 'flex justify-between' : 'text-left',
          isError && 'border-red-500',
        )}
      >
        {options.length === 0 && !isLoading ? (
          <span className="text-red-500">
            <FormattedMessage
              id="beacon.select.empty.beacon"
              defaultMessage="No beacon"
            />
          </span>
        ) : hasValue && !isLoading ? (
          renderSelectedItems() // Call the function to render selected items
        ) : (
          <span>
            <FormattedMessage id="create.beacon.message.selector.placeholder" />
          </span>
        )}
        {isLoading && <CircularProgress color="inherit" size={20} />}
      </button>

      {isOpen && options.length > 0 && (
        <ul
          className={clsx(
            'absolute z-10 w-full bg-white shadow rounded-4 text-14 overflow-y-auto h-auto focus:outline-none p-0',
            classes.dropdown,
          )}
        >
          <li
            className="cursor-default select-none relative py-2 pl-10 pr-4 contents"
            onClick={handleSelectAll}
          >
            <div className="flex items-center h-56 space-x-5 border-b-1 px-5 border-gray-200">
              <CheckBox
                type="checkbox"
                className="p-0"
                key="check-all"
                disableRipple
                color="default"
                checked={isAllSelected}
                onChange={handleSelectAll}
                inputProps={{
                  'aria-label': `Select all`,
                  'data-testid': `beacon-all`,
                }}
              />
              <span className="ml-3 block truncate font-medium">
                <FormattedMessage id="create.beacon.message.selector.all.placeholder" />
              </span>
            </div>
          </li>

          {options.map((option) => (
            <li
              key={option.id}
              className="cursor-default select-none relative py-2 pl-10 pr-4 contents"
              onClick={() => handleSelect(option)}
            >
              <div className="border-b-1 border-gray-200 px-32 h-full">
                <div className="flex items-center h-56 space-x-5">
                  <CheckBox
                    className="p-0"
                    key={`check-${option.id}`}
                    disableRipple
                    checked={field.value.some((o) => o.id === option.id)}
                    color="default"
                    onChange={() => handleSelect(option)}
                    inputProps={{
                      'aria-label': `Select ${option.name}`,
                      'data-testid': `beacon${option.id}`,
                    }}
                  />
                  <span
                    className={`ml-3 block truncate m-auto ${
                      field.value.some((o) => o.id === option.id)
                        ? 'font-medium'
                        : 'font-normal'
                    }`}
                  >
                    {option.name}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {isError && (
        <div className="flex space-x-2 py-2">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red text-14">{meta.error}</label>
        </div>
      )}
    </div>
  )
}

// Add propTypes for prop validation
BeaconSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired, // Array of beacon options
  name: PropTypes.string.isRequired, // Name for formik field
  fetchData: PropTypes.func.isRequired, // Function to fetch beacon data
  isLoading: PropTypes.bool.isRequired, // Loading state
}

export default React.memo(BeaconSelector)
